import React, { useState, useEffect, useRef } from "react";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { makeStyles } from "@material-ui/core/styles";

// Tour "estado da arte" (spotlight): anel pulsante NO NÓ real do canvas, com
// pan automático até ele, abertura da config e AUTO-AVANÇO quando o campo é
// preenchido. Não-modal (a config segue usável). Intro + passos + concluído.
const useStyles = makeStyles(theme => ({
  "@keyframes pulse": {
    "0%,100%": { boxShadow: "0 0 0 3px rgba(206,255,0,.95), 0 0 22px 5px rgba(206,255,0,.45)" },
    "50%": { boxShadow: "0 0 0 3px rgba(206,255,0,.65), 0 0 34px 10px rgba(206,255,0,.7)" },
  },
  ring: { position: "fixed", borderRadius: 12, pointerEvents: "none", zIndex: 1290, animation: "$pulse 1.4s ease-in-out infinite", transition: "top .28s cubic-bezier(.4,0,.2,1), left .28s cubic-bezier(.4,0,.2,1), width .28s, height .28s" },
  ringBadge: { position: "absolute", top: -12, left: -6, height: 22, minWidth: 22, padding: "0 8px", borderRadius: 11, background: "#2C353D", color: "#CEFF00", fontSize: 11, fontWeight: 700, letterSpacing: "0.02em", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(20,20,20,.3)", whiteSpace: "nowrap" },
  ringBadgeOk: { background: theme.app.ok, color: "#fff" },
  pop: { position: "fixed", width: 320, background: "#fff", borderRadius: 14, border: `1px solid ${theme.app.border}`, boxShadow: "0 18px 50px rgba(20,20,20,.26)", zIndex: 1300, overflow: "hidden", transition: "top .28s, left .28s" },
  center: { position: "fixed", left: "50%", bottom: 34, transform: "translateX(-50%)", width: 380 },
  head: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 10px 6px 16px" },
  kicker: { fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: theme.app.textTertiary, display: "flex", alignItems: "center", gap: 6 },
  body: { padding: "0 16px 14px" },
  title: { fontSize: 15, fontWeight: 600, color: theme.app.ink, marginBottom: 6, lineHeight: 1.35 },
  desc: { fontSize: 13, color: theme.app.textSecondary, lineHeight: 1.5 },
  pre: { display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12.5, color: theme.app.ink, padding: "4px 0" },
  preDot: { width: 6, height: 6, borderRadius: "50%", background: theme.app.textTertiary, marginTop: 6, flexShrink: 0 },
  preArea: { color: theme.app.textTertiary },
  foot: { display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderTop: `1px solid ${theme.app.lineSoft}`, background: "#FAFAF7" },
  dots: { display: "flex", gap: 5, flex: 1 },
  dot: { width: 6, height: 6, borderRadius: "50%", background: theme.app.border, transition: "background .2s" },
  dotOn: { background: theme.app.dark },
  dotDone: { background: theme.app.ok },
  link: { fontSize: 12.5, color: theme.app.textTertiary, cursor: "pointer", "&:hover": { color: theme.app.ink } },
  doneRow: { display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: theme.app.ok, "& svg": { fontSize: 20 } },
  okChip: { color: theme.app.ok, display: "flex", alignItems: "center", gap: 4, fontSize: 11.5, fontWeight: 600 },
}));

const nodeEl = id => {
  try { return document.querySelector(`.react-flow__node[data-id="${id}"]`); } catch (e) { return null; }
};
const paneRect = () => {
  const p = document.querySelector(".react-flow");
  return p ? p.getBoundingClientRect() : null;
};
// retângulo do nó em coordenadas de TELA. Tenta o DOM (preciso); se o nó não
// estiver no DOM, calcula via transform do react-flow + posição do nó (robusto:
// nunca devolve null por falha de query, só se o nó não existir).
const nodeScreenRect = (id, elements, rfInstance) => {
  const el = nodeEl(id);
  if (el) {
    const r = el.getBoundingClientRect();
    if (r.width && r.height) return r;
  }
  const pr = paneRect();
  const node = (elements || []).find(e => e && e.id === id);
  if (!pr || !node || !node.position || !rfInstance) return null;
  let zoom = 1, tx = 0, ty = 0;
  try {
    const o = rfInstance.toObject ? rfInstance.toObject() : null;
    if (o) { zoom = o.zoom || 1; tx = (o.position || [0, 0])[0]; ty = (o.position || [0, 0])[1]; }
  } catch (e) { /* defaults */ }
  const rf = node.__rf || {};
  const w = (rf.width || 200) * zoom;
  const h = (rf.height || 64) * zoom;
  const left = pr.left + tx + node.position.x * zoom;
  const top = pr.top + ty + node.position.y * zoom;
  return { left, top, right: left + w, bottom: top + h, width: w, height: h };
};
// um passo (todo) está resolvido quando NENHUM dos campos que ele observa
// (`tracks`) continua pendente. Passos sem `tracks` (orientação pura) nunca
// auto-resolvem — o usuário avança manualmente com "Próximo".
const stepResolved = (cur, pending) =>
  cur && cur.kind === "todo" && Array.isArray(cur.tracks) && cur.tracks.length > 0 &&
  !cur.tracks.some(f => (pending || []).some(p => p.nodeId === cur.nodeId && p.field === f));

const SetupTour = ({ open, setup, steps: tourSteps, pending, elements, onFocus, onClose, rfInstance }) => {
  const classes = useStyles();
  const [idx, setIdx] = useState(0);
  const [steps, setSteps] = useState([]);
  const [rect, setRect] = useState(null);
  const built = useRef(false);
  // se o passo estava pendente AO ENTRAR: só então o auto-avanço dispara quando
  // ele é resolvido (evita pular passos já configurados que o usuário quer ler).
  const enteredPending = useRef(false);

  // sequência congelada ao abrir (intro + passos autorados/pendências + concluído)
  useEffect(() => {
    if (!open) { built.current = false; return; }
    if (built.current) return;
    built.current = true;
    const s = setup || {};
    const seq = [];
    if (s.howItWorks || (Array.isArray(s.prerequisites) && s.prerequisites.length)) seq.push({ kind: "intro" });
    (tourSteps || []).forEach(p => seq.push({ ...p, kind: "todo" }));
    seq.push({ kind: "done" });
    setSteps(seq);
    setIdx(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cur = steps[idx];

  // ao entrar num passo de nó: abre a config e, SÓ se o nó estiver fora da área
  // visível (atrás do painel à direita / fora da tela), faz um pan MÍNIMO para
  // trazê-lo a uma zona confortável. Não recentraliza — assim cada passo mantém
  // o nó na sua posição própria (dá pra distinguir um do outro).
  useEffect(() => {
    enteredPending.current = stepResolved(cur, pending) === false && Array.isArray(cur && cur.tracks) && cur.tracks.length > 0;
    if (!open || !cur || cur.kind !== "todo" || !cur.nodeId) return;
    if (onFocus) onFocus(cur.nodeId);
    const t = setTimeout(() => {
      try {
        const pr = paneRect();
        const nr = nodeScreenRect(cur.nodeId, elements, rfInstance);
        if (!pr || !nr || !rfInstance || !rfInstance.setTransform) return;
        const mx = 70, top = 90, bottom = 250; // folga p/ painel/topo/card
        const zL = pr.left + mx, zR = pr.right - mx, zT = pr.top + top, zB = pr.bottom - bottom;
        let dx = 0, dy = 0;
        if (nr.left < zL) dx = zL - nr.left; else if (nr.right > zR) dx = zR - nr.right;
        if (nr.top < zT) dy = zT - nr.top; else if (nr.bottom > zB) dy = zB - nr.bottom;
        if (!dx && !dy) return; // já visível: não mexe no canvas
        const o = rfInstance.toObject ? rfInstance.toObject() : null;
        const cz = o ? o.zoom : 1;
        const cp = o ? o.position : [0, 0];
        rfInstance.setTransform({ x: cp[0] + dx, y: cp[1] + dy, zoom: cz });
      } catch (e) { /* best-effort */ }
    }, 160); // espera a config abrir (a largura do canvas muda)
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, open]);

  // segue o nó (pan/zoom/scroll) — recalcula o rect do anel a cada frame
  useEffect(() => {
    if (!open) return undefined;
    let raf;
    const tick = () => {
      if (cur && cur.kind === "todo" && cur.nodeId) {
        setRect(nodeScreenRect(cur.nodeId, elements, rfInstance));
      } else setRect(null);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idx, cur && cur.nodeId]);

  // AUTO-AVANÇO: só quando o passo ESTAVA pendente ao entrar e foi preenchido
  const resolved = stepResolved(cur, pending);
  useEffect(() => {
    if (!open || !cur || cur.kind !== "todo" || !resolved || !enteredPending.current) return undefined;
    const t = setTimeout(() => setIdx(i => Math.min(i + 1, steps.length - 1)), 750);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved, idx, open]);

  if (!open || !cur) return null;

  const todos = steps.filter(s => s.kind === "todo");
  const todoIndex = cur.kind === "todo" ? todos.indexOf(cur) + 1 : 0;
  const last = idx >= steps.length - 1;
  const next = () => (last ? onClose() : setIdx(i => i + 1));
  const prev = () => setIdx(i => Math.max(0, i - 1));
  const s = setup || {};

  // posição do popover: junto do nó (acima/abaixo conforme espaço); senão centro
  let popStyle = null;
  if (cur.kind === "todo" && rect) {
    const below = rect.bottom + 14 + 200 < window.innerHeight;
    const top = below ? rect.bottom + 14 : Math.max(12, rect.top - 14 - 200);
    let left = rect.left + rect.width / 2 - 160;
    left = Math.max(12, Math.min(left, window.innerWidth - 332));
    popStyle = { top, left };
  }

  const dotCls = i => {
    const st = steps[i];
    const done = st.kind === "todo" && (stepResolved(st, pending) || i < idx);
    if (done) return `${classes.dot} ${classes.dotDone}`;
    return `${classes.dot} ${i === idx ? classes.dotOn : ""}`;
  };

  return (
    <>
      {cur.kind === "todo" && rect && (
        <div
          className={classes.ring}
          style={{ top: rect.top - 5, left: rect.left - 5, width: rect.width + 10, height: rect.height + 10, ...(resolved ? { animation: "none", boxShadow: "0 0 0 3px rgba(43,138,62,.9)" } : {}) }}
        >
          <span className={`${classes.ringBadge} ${resolved ? classes.ringBadgeOk : ""}`}>
            {resolved ? <CheckCircleIcon style={{ fontSize: 15 }} /> : `Passo ${todoIndex}`}
          </span>
        </div>
      )}

      <div className={popStyle ? classes.pop : `${classes.pop} ${classes.center}`} style={popStyle || undefined}>
        <div className={classes.head}>
          <span className={classes.kicker}>
            {cur.kind === "intro" ? "Primeiros passos" : cur.kind === "done" ? "Pronto" : `Passo ${todoIndex} de ${todos.length}`}
            {resolved && <span className={classes.okChip}><CheckCircleIcon style={{ fontSize: 14 }} /> feito</span>}
          </span>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </div>

        <div className={classes.body}>
          {cur.kind === "intro" && (
            <>
              {s.howItWorks ? <div className={classes.desc} style={{ marginBottom: 10 }}>{s.howItWorks}</div> : null}
              {Array.isArray(s.prerequisites) && s.prerequisites.length > 0 && (
                <>
                  <div className={classes.kicker} style={{ margin: "4px 0 4px" }}>Pré-requisitos</div>
                  {s.prerequisites.map((p, i) => (
                    <div key={i} className={classes.pre}>
                      <span className={classes.preDot} />
                      <span>{p.label}{p.area ? <span className={classes.preArea}> · {p.area}</span> : null}</span>
                    </div>
                  ))}
                </>
              )}
              {todos.length > 0 && <div className={classes.desc} style={{ marginTop: 10 }}>Vou te guiar por cada passo de configuração ({todos.length}).</div>}
            </>
          )}

          {cur.kind === "todo" && (
            <>
              <div className={classes.title}>{cur.title}</div>
              <div className={classes.desc}>
                {resolved
                  ? "Feito! Avançando…"
                  : <>{cur.description}{cur.node ? <> No nó <b>{cur.node}</b> (destacado), preencha o painel à direita.</> : null}</>}
              </div>
            </>
          )}

          {cur.kind === "done" && (
            <div className={classes.doneRow}><CheckCircleIcon /> Tudo configurado! Salve e publique quando quiser.</div>
          )}
        </div>

        <div className={classes.foot}>
          <div className={classes.dots}>{steps.map((_, i) => <span key={i} className={dotCls(i)} />)}</div>
          {idx > 0 && cur.kind !== "done" && <span className={classes.link} onClick={prev}>Voltar</span>}
          {cur.kind !== "done" && <span className={classes.link} onClick={onClose}>Sair</span>}
          <Button size="small" variant="contained" color="primary" disableElevation onClick={next}>
            {cur.kind === "intro" ? "Começar" : last ? "Concluir" : "Próximo"}
          </Button>
        </div>
      </div>
    </>
  );
};

export default SetupTour;
